import _asyncToGenerator from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["appearance", "className", "before", "after", "overrideConsent", "customFormIdentifier", "successMessageAppearance", "email", "successMessage"];
import _regeneratorRuntime from "/workdir/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppearance, AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext';
import Button from '@segment/matcha/components/Button';
import { CustomFormIdentifierField } from '@segment/templates/FormFields';
import Disclaimer from '@segment/templates/Disclaimer';
import FormSubmitAction from '@segment/templates/FormSubmitAction';
import { JobTitleField, jobTitleMessages } from '@segment/templates/FormFields/JobTitle';
import { timelineMessages, TimelineSelectField } from '@segment/templates/FormFields/Timeline';
import { monthlyTrafficMessages, MonthlyTrafficSelectField } from '@segment/templates/FormFields/MonthlyTraffic';
import { companySizeMessages, CompanySizeSelectField } from '@segment/templates/FormFields/CompanySize';
import { HiddenEmailField } from '@segment/templates/FormFields/HiddenEmail';
import TrackableForm, { errorMessage } from '@segment/templates/TrackableForm';
import { trackButtonClick, trackProgressiveProfileForm, trackValidationErrorProgressiveProfileForm } from '@segment/tracking/scripts/helpers';
import useScrollToAnchor from '@segment/utils/useScrollToAnchor';
import formRegex from '@segment/utils/regexes/form';
import { DemoContext } from '@segment/contexts/demo';
import { usePageConfig } from '@segment/contexts/page';
import * as views from './ProgressiveProfileForm.views';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
var BUTTON_TEXT = "Let's get started";
/* eslint-disable camelcase */

var schema = z.object({
  jobTitle: z.string({
    required_error: jobTitleMessages.required
  }).regex(formRegex, {
    message: jobTitleMessages.invalid
  }),
  companySize: z.string({
    required_error: companySizeMessages.required
  }),
  monthlyTraffic: z.string({
    required_error: monthlyTrafficMessages.required
  }),
  timeline: z.string({
    required_error: timelineMessages.required
  }),
  email: z.string().optional()
});
/* eslint-enable camelcase */
// Set up demo form props since we define some of these props here in the template

export var ProgressiveProfileForm = function ProgressiveProfileForm(_ref) {
  var appearance = _ref.appearance,
      className = _ref.className,
      before = _ref.before,
      after = _ref.after,
      _ref$overrideConsent = _ref.overrideConsent,
      overrideConsent = _ref$overrideConsent === void 0 ? true : _ref$overrideConsent,
      customFormIdentifier = _ref.customFormIdentifier,
      successMessageAppearance = _ref.successMessageAppearance,
      email = _ref.email,
      customSuccessMessage = _ref.successMessage,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _usePageConfig = usePageConfig(),
      baseUrl = _usePageConfig.baseUrl;

  var formAppearance = useAppearance(appearance);
  var scrollTo = useScrollToAnchor("progressive-profile-form", -200);

  var _useContext = useContext(DemoContext),
      setCompletedFormData = _useContext.setCompletedFormData; // Initialize form here since we need the form initialized to handle progressive fields and the personal email functionality


  var methods = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUnregister: true,
    resolver: zodResolver(schema)
  });
  var successMessage = customSuccessMessage || {
    heading: "Thank you, you\u2019re all set!",
    description: "We'll get back to you shortly. For now, you can create your workspace by clicking below.",
    buttonUrl: "".concat(baseUrl, "/signup/?utm_campaign=demo"),
    buttonText: "Create your free workspace"
  };
  return _jsx(AppearanceProvider, {
    appearance: formAppearance,
    children: _jsx(views.FormWrapper, {
      id: "progressive-profile-form",
      children: _jsxs(TrackableForm, _objectSpread(_objectSpread({
        gap: "small",
        methods: methods,
        className: className,
        before: before,
        after: after,
        success: _jsx(FormSubmitAction, _objectSpread({
          form: "demo",
          status: "success",
          appearance: successMessageAppearance,
          "data-hook": "demo-signup-success-message",
          onButtonClick: function onButtonClick() {
            trackButtonClick({
              buttonText: successMessage.buttonText,
              buttonId: "Workspace signup",
              targetUrl: successMessage.buttonUrl
            });
          }
        }, successMessage)),
        error: _jsx(FormSubmitAction, _objectSpread({
          status: "error"
        }, errorMessage)),
        onSubmit: /*#__PURE__*/function () {
          var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(data) {
            return _regeneratorRuntime.wrap(function _callee$(_context) {
              while (1) {
                switch (_context.prev = _context.next) {
                  case 0:
                    setCompletedFormData(data);
                    _context.next = 3;
                    return trackProgressiveProfileForm(data);

                  case 3:
                    scrollTo();

                  case 4:
                  case "end":
                    return _context.stop();
                }
              }
            }, _callee);
          }));

          return function (_x) {
            return _ref2.apply(this, arguments);
          };
        }(),
        onFailure: trackValidationErrorProgressiveProfileForm,
        overrideConsent: overrideConsent,
        customFormIdentifier: customFormIdentifier
      }, rest), {}, {
        children: [_jsx(JobTitleField, {}), _jsx(CompanySizeSelectField, {}), _jsx(MonthlyTrafficSelectField, {}), _jsx(TimelineSelectField, {}), customFormIdentifier && _jsx(CustomFormIdentifierField, {
          value: customFormIdentifier
        }), email && _jsx(HiddenEmailField, {
          value: email
        }), _jsx(views.ButtonWrapper, {
          children: _jsx(Button, {
            color: "primary",
            variant: "filled",
            size: "large",
            expanded: true,
            children: BUTTON_TEXT
          })
        }), _jsx(Disclaimer, {
          appearance: successMessageAppearance
        })]
      }))
    })
  });
};
export default ProgressiveProfileForm;